import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';

import {C} from '../../contentReview/constants';
import {fetchGmcAccounts, fetchGmcDataSources, registerGmcDeveloper} from './gmcService';

const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: C.textSoft,
    textTransform: 'uppercase',
    letterSpacing: '.06em',
};

const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 800,
    color: C.ink,
};

const compactInputProps = {
    sx: {
        fontSize: 13,
        background: C.surface,
        '& .MuiOutlinedInput-notchedOutline': {borderColor: C.border},
    },
};

const compactSelectSx = {
    fontSize: 13,
    background: C.surface,
    '& .MuiOutlinedInput-notchedOutline': {borderColor: C.border},
};

const formatAccountLabel = (account) => {
    if (!account) return '';
    const name = account.displayName || 'Untitled account';
    const idPart = account.id ? ` (${account.id})` : '';
    const testPart = account.testAccount ? ' · test' : '';
    return `${name}${idPart}${testPart}`;
};

// Detects Google's "GCP project not registered" error and extracts the
// project ID and number from the message so we can show them in a panel.
// Returns null when the error is something else.
const REGISTRATION_ERROR_PATTERN = /gcp project.*not registered with the merchant account/i;
const parseRegistrationError = (message) => {
    if (typeof message !== 'string' || !REGISTRATION_ERROR_PATTERN.test(message)) {
        return null;
    }
    const idMatch = message.match(/with id ([\w-]+)/i);
    const numberMatch = message.match(/number (\d+)/i);
    return {
        projectId: idMatch?.[1] || '',
        projectNumber: numberMatch?.[1] || '',
    };
};

export default function GmcSettingsDialog({open, onClose, gmc}) {
    const {status, loading, error, refresh, connect, disconnect, updateSettings} = gmc;
    const [draft, setDraft] = useState(status.settings);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveOk, setSaveOk] = useState(false);
    const [accounts, setAccounts] = useState({data: [], loading: false, error: null});
    const [dataSources, setDataSources] = useState({data: [], loading: false, error: null});
    const [accountsRetryCounter, setAccountsRetryCounter] = useState(0);
    const [copyFeedback, setCopyFeedback] = useState('');
    const [registerMerchantId, setRegisterMerchantId] = useState('');
    const [registerState, setRegisterState] = useState({status: 'idle', message: ''});
    // Tracks the merchant ID we last fetched data sources for so we don't
    // re-fetch on unrelated draft changes (only when merchantId itself moves).
    const lastFetchedMerchantId = useRef(null);

    useEffect(() => {
        if (open) setDraft(status.settings);
    }, [open, status.settings]);

    // Fetch accessible Merchant Center accounts whenever the dialog opens
    // while connected, or when the user clicks the registration "Retry"
    // button (which bumps accountsRetryCounter). Auto-fills the merchant ID
    // field if there's exactly one account and the field is currently empty.
    useEffect(() => {
        if (!open || !status.connected) {
            setAccounts({data: [], loading: false, error: null});
            return undefined;
        }

        let cancelled = false;
        setAccounts((current) => ({...current, loading: true, error: null}));

        fetchGmcAccounts()
            .then((data) => {
                if (cancelled) return;
                setAccounts({data, loading: false, error: null});
                if (data.length === 1) {
                    setDraft((current) => (current.merchantId ? current : {...current, merchantId: data[0].id}));
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setAccounts({data: [], loading: false, error: err.message});
            });

        return () => {
            cancelled = true;
        };
    }, [open, status.connected, accountsRetryCounter]);

    // Fetch supplemental data sources whenever the merchant ID changes (and
    // we have one). Builds the feedSources map: every supplemental source
    // we find becomes a route keyed by its feed label. The push flow picks
    // the right one per product at runtime based on the offer ID.
    useEffect(() => {
        if (!open || !status.connected || !draft.merchantId) {
            setDataSources({data: [], loading: false, error: null});
            lastFetchedMerchantId.current = null;
            return undefined;
        }

        if (lastFetchedMerchantId.current === draft.merchantId) {
            return undefined;
        }
        lastFetchedMerchantId.current = draft.merchantId;

        let cancelled = false;
        setDataSources((current) => ({...current, loading: true, error: null}));

        fetchGmcDataSources(draft.merchantId)
            .then((data) => {
                if (cancelled) return;
                setDataSources({data, loading: false, error: null});

                // Build a feedSources map from every supplemental data
                // source with a non-empty feedLabel. First-writer-wins if a
                // merchant somehow has two sources for the same feed label.
                const nextFeedSources = {};
                data.forEach((source) => {
                    if (!source?.feedLabel || !source?.id) return;
                    if (nextFeedSources[source.feedLabel]) return;
                    nextFeedSources[source.feedLabel] = {
                        dataSourceId: source.id,
                        contentLanguage: source.contentLanguage || '',
                        displayName: source.displayName || '',
                    };
                });

                setDraft((current) => ({
                    ...current,
                    feedSources: nextFeedSources,
                    // Mirror the first source into the legacy fields so
                    // older callers (if any) still work and the settings
                    // serialise consistently.
                    supplementalFeedId: data[0]?.id || current.supplementalFeedId || '',
                    targetCountry: data[0]?.feedLabel || current.targetCountry,
                    contentLanguage: data[0]?.contentLanguage || current.contentLanguage,
                }));
            })
            .catch((err) => {
                if (cancelled) return;
                setDataSources({data: [], loading: false, error: err.message});
            });

        return () => {
            cancelled = true;
        };
    }, [open, status.connected, draft.merchantId]);

    const dirty = useMemo(
        () => JSON.stringify(draft) !== JSON.stringify(status.settings),
        [draft, status.settings],
    );

    const handleField = (field) => (event) => {
        setDraft((current) => ({...current, [field]: event.target.value}));
        setSaveOk(false);
    };

    // Changing the merchant ID resets everything that was derived from the
    // previous merchant's data sources.
    const handleMerchantChange = useCallback((nextValue) => {
        setDraft((current) => {
            if (current.merchantId === nextValue) return current;
            return {
                ...current,
                merchantId: nextValue,
                supplementalFeedId: '',
                feedSources: {},
            };
        });
        setSaveOk(false);
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        setSaveOk(false);
        try {
            await updateSettings(draft);
            setSaveOk(true);
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            await disconnect();
        } catch (err) {
            setSaveError(err.message);
        }
    };

    const retryAccounts = useCallback(() => {
        setAccountsRetryCounter((counter) => counter + 1);
    }, []);

    const handleRegisterDeveloper = useCallback(async () => {
        const merchantId = registerMerchantId.trim();
        if (!merchantId) {
            setRegisterState({status: 'error', message: 'Enter your Merchant Center ID first.'});
            return;
        }
        setRegisterState({status: 'loading', message: ''});
        try {
            await registerGmcDeveloper(merchantId);
            setRegisterState({
                status: 'success',
                message: `Registered. Wait ~5 minutes for Google to propagate, then click Retry.`,
            });
            // Pre-fill the merchant ID in the main draft so the user doesn't
            // have to type it again after retry succeeds.
            setDraft((current) => (current.merchantId ? current : {...current, merchantId}));
        } catch (err) {
            setRegisterState({status: 'error', message: err.message});
        }
    }, [registerMerchantId]);

    const copyToClipboard = useCallback((value) => {
        if (!value || typeof navigator === 'undefined' || !navigator.clipboard) return;
        navigator.clipboard
            .writeText(value)
            .then(() => {
                setCopyFeedback(value);
                setTimeout(() => setCopyFeedback(''), 1500);
            })
            .catch(() => {
                /* clipboard not available — ignore */
            });
    }, []);

    const registrationError = parseRegistrationError(accounts.error);

    const showOAuthMissing = !loading && !status.hasOAuthCredentials;

    const renderMerchantField = () => {
        if (accounts.loading) {
            return (
                <Stack direction="row" alignItems="center" gap="8px" sx={{padding: '6px 0'}}>
                    <CircularProgress size={14} />
                    <Typography sx={{fontSize: 12, color: C.textSoft}}>
                        Loading accounts…
                    </Typography>
                </Stack>
            );
        }

        if (accounts.data.length > 1) {
            return (
                <FormControl fullWidth size="small">
                    <Select
                        value={accounts.data.some((account) => account.id === draft.merchantId) ? draft.merchantId : ''}
                        onChange={(event) => handleMerchantChange(event.target.value)}
                        displayEmpty
                        sx={compactSelectSx}>
                        <MenuItem value="" disabled>
                            <em>Select a merchant account…</em>
                        </MenuItem>
                        {accounts.data.map((account) => (
                            <MenuItem key={account.id} value={account.id}>
                                {formatAccountLabel(account)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        return (
            <TextField
                fullWidth
                size="small"
                placeholder="e.g. 1234567890"
                value={draft.merchantId}
                onChange={(event) => handleMerchantChange(event.target.value)}
                InputProps={compactInputProps}
            />
        );
    };

    const renderMerchantHelper = () => {
        if (accounts.error) {
            // Registration errors get the dedicated panel above; don't
            // duplicate them here.
            if (registrationError) return null;
            return (
                <Typography sx={{fontSize: 11, color: C.coral, marginTop: '4px'}}>
                    Could not load accounts: {accounts.error}
                </Typography>
            );
        }
        if (accounts.loading) return null;
        if (accounts.data.length === 1) {
            return (
                <Typography sx={{fontSize: 11, color: C.textFaint, marginTop: '4px'}}>
                    Auto-filled from your Google account ({accounts.data[0].id}).
                </Typography>
            );
        }
        if (accounts.data.length > 1) {
            return (
                <Typography sx={{fontSize: 11, color: C.textFaint, marginTop: '4px'}}>
                    Found {accounts.data.length} accounts. Pick the one you want to push to.
                </Typography>
            );
        }
        return null;
    };

    const renderDetectedSources = () => {
        if (!draft.merchantId) {
            return (
                <Box
                    sx={{
                        padding: '10px 12px',
                        border: `1px dashed ${C.border}`,
                        borderRadius: '10px',
                        background: C.surface,
                    }}>
                    <Typography sx={{fontSize: 12, color: C.textFaint}}>
                        Set a merchant ID first to detect supplemental data sources.
                    </Typography>
                </Box>
            );
        }

        if (dataSources.loading) {
            return (
                <Stack direction="row" alignItems="center" gap="8px" sx={{padding: '8px 12px'}}>
                    <CircularProgress size={14} />
                    <Typography sx={{fontSize: 12, color: C.textSoft}}>
                        Loading data sources…
                    </Typography>
                </Stack>
            );
        }

        if (dataSources.error) {
            if (registrationError || parseRegistrationError(dataSources.error)) return null;
            return (
                <Typography sx={{fontSize: 11, color: C.coral, marginTop: '4px'}}>
                    Could not load data sources: {dataSources.error}
                </Typography>
            );
        }

        if (dataSources.data.length === 0) {
            return (
                <Box
                    sx={{
                        padding: '10px 12px',
                        border: `1px solid ${C.amber}33`,
                        borderRadius: '10px',
                        background: C.amberL,
                    }}>
                    <Typography sx={{fontSize: 12, color: C.amber, lineHeight: 1.5}}>
                        No supplemental data source found in this account. In Merchant Center → Data
                        sources, add a supplemental product source (API) for each feed label you want
                        to override, then reopen this dialog.
                    </Typography>
                </Box>
            );
        }

        return (
            <Stack gap="6px">
                {dataSources.data.map((source) => (
                    <Box
                        key={source.id}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr auto',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 12px',
                            border: `1px solid ${C.border}`,
                            borderRadius: '10px',
                            background: C.surface,
                        }}>
                        <Box
                            sx={{
                                fontSize: 10,
                                fontWeight: 800,
                                color: C.teal,
                                background: C.tealL,
                                border: `1px solid ${C.teal}33`,
                                padding: '3px 8px',
                                borderRadius: 999,
                                letterSpacing: '.04em',
                                textTransform: 'uppercase',
                            }}>
                            {source.feedLabel || '—'}
                            {source.contentLanguage ? ` / ${source.contentLanguage}` : ''}
                        </Box>
                        <Typography
                            sx={{
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: C.ink,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            title={source.displayName || source.id}>
                            {source.displayName || 'Untitled data source'}
                        </Typography>
                        <Typography sx={{fontSize: 10.5, color: C.textFaint, fontFamily: 'ui-monospace, Menlo, monospace'}}>
                            {source.id}
                        </Typography>
                    </Box>
                ))}
                <Typography sx={{fontSize: 11, color: C.textFaint, marginTop: '4px', lineHeight: 1.5}}>
                    {dataSources.data.length === 1
                        ? 'Every push will route to this data source.'
                        : `Pushes are routed automatically — each product goes to the source matching its feed label (parsed from the offer ID).`}
                </Typography>
            </Stack>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16, fontWeight: 800}}>
                Google Merchant Center
                <Typography sx={{fontSize: 11.5, color: C.textSoft, marginTop: '2px'}}>
                    Push title and description overrides via a supplemental data source (Merchant API).
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{background: C.surfaceAlt}}>
                {loading ? (
                    <Stack direction="row" alignItems="center" gap="10px" sx={{padding: '12px 0'}}>
                        <CircularProgress size={16} />
                        <Typography sx={{fontSize: 13, color: C.textSoft}}>
                            Loading status…
                        </Typography>
                    </Stack>
                ) : (
                    <Stack gap="18px">
                        {showOAuthMissing && (
                            <Alert severity="warning" sx={{fontSize: 12.5}}>
                                Google OAuth credentials are not configured on the server.
                                Set <code>GMC_OAUTH_CLIENT_ID</code>, <code>GMC_OAUTH_CLIENT_SECRET</code>,
                                and <code>GMC_OAUTH_REDIRECT_URI</code> in your <code>.env</code>, then restart the server.
                            </Alert>
                        )}

                        {error && (
                            <Alert severity="error" sx={{fontSize: 12.5}}>{error}</Alert>
                        )}

                        <Box>
                            <Typography sx={sectionTitleStyle}>Connection</Typography>
                            <Box
                                sx={{
                                    marginTop: '8px',
                                    padding: '12px',
                                    border: `1px solid ${C.border}`,
                                    borderRadius: '10px',
                                    background: C.surface,
                                }}>
                                {status.connected ? (
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap="10px">
                                        <Box>
                                            <Typography sx={{fontSize: 12, color: C.textFaint}}>Signed in as</Typography>
                                            <Typography sx={{fontSize: 13.5, fontWeight: 700, color: C.ink}}>
                                                {status.email || 'Google account'}
                                            </Typography>
                                            {status.connectedAt && (
                                                <Typography sx={{fontSize: 11, color: C.textFaint, marginTop: '2px'}}>
                                                    Connected {new Date(status.connectedAt).toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Stack direction="row" gap="8px">
                                            <Tooltip title="Refresh status">
                                                <span>
                                                    <IconButton size="small" onClick={refresh}>
                                                        <Box component="span" sx={{fontSize: 14}}>↻</Box>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Button size="small" onClick={handleDisconnect} color="inherit">
                                                Disconnect
                                            </Button>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap="10px">
                                        <Typography sx={{fontSize: 13, color: C.textSoft}}>
                                            Not connected to a Google account.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={connect}
                                            disabled={showOAuthMissing}>
                                            Connect Google
                                        </Button>
                                    </Stack>
                                )}
                            </Box>
                        </Box>

                        <Divider />

                        {registrationError && (
                            <Alert
                                severity="warning"
                                sx={{
                                    fontSize: 12.5,
                                    alignItems: 'flex-start',
                                    '& .MuiAlert-message': {flex: 1, paddingBottom: 0},
                                }}>
                                <Typography sx={{fontSize: 13, fontWeight: 800, color: C.ink, marginBottom: '6px'}}>
                                    One-time setup needed in Merchant Center
                                </Typography>
                                <Typography sx={{fontSize: 12.5, color: C.textSoft, lineHeight: 1.5, marginBottom: '12px'}}>
                                    Google requires you to authorize this app&apos;s GCP project in your Merchant Center account before it can list accounts or push products. This is a one-time, ~30-second step.
                                </Typography>

                                {registrationError.projectNumber && (
                                    <Box
                                        sx={{
                                            padding: '10px 12px',
                                            background: C.surface,
                                            border: `1px solid ${C.border}`,
                                            borderRadius: '8px',
                                            marginBottom: '12px',
                                        }}>
                                        <Typography sx={labelStyle}>GCP project number</Typography>
                                        <Stack direction="row" alignItems="center" gap="8px" sx={{marginTop: '4px'}}>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                    fontWeight: 700,
                                                    color: C.ink,
                                                    flex: 1,
                                                }}>
                                                {registrationError.projectNumber}
                                            </Typography>
                                            <Button
                                                size="small"
                                                onClick={() => copyToClipboard(registrationError.projectNumber)}
                                                sx={{fontSize: 11, minWidth: 64}}>
                                                {copyFeedback === registrationError.projectNumber ? 'Copied' : 'Copy'}
                                            </Button>
                                        </Stack>
                                        {registrationError.projectId && (
                                            <Typography sx={{fontSize: 11, color: C.textFaint, marginTop: '4px'}}>
                                                Project ID: <code>{registrationError.projectId}</code>
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                <Typography sx={{fontSize: 12, color: C.textSoft, marginBottom: '8px'}}>
                                    Steps:
                                </Typography>
                                <Box component="ol" sx={{margin: 0, paddingLeft: '18px', fontSize: 12, color: C.textSoft, lineHeight: 1.6}}>
                                    <li>Open the link below and pick the merchant account you want to use.</li>
                                    <li>Paste the GCP project number above into the registration field.</li>
                                    <li>Click <strong>Register</strong>, then wait ~5 minutes for Google to propagate.</li>
                                    <li>Come back here and click <strong>Retry</strong>.</li>
                                </Box>

                                <Stack direction="row" gap="8px" sx={{marginTop: '12px', flexWrap: 'wrap'}}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        component="a"
                                        href="https://merchants.google.com/mc/developerregistration"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{fontSize: 12}}>
                                        Open Merchant Center → Developer registration ↗
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={retryAccounts}
                                        disabled={accounts.loading}
                                        sx={{fontSize: 12}}>
                                        {accounts.loading ? 'Retrying…' : 'Retry'}
                                    </Button>
                                </Stack>

                                <Divider sx={{margin: '14px 0 12px'}} />

                                <Typography sx={{fontSize: 12, fontWeight: 700, color: C.ink, marginBottom: '4px'}}>
                                    Can&apos;t find the registration form in Merchant Center?
                                </Typography>
                                <Typography sx={{fontSize: 11.5, color: C.textSoft, lineHeight: 1.5, marginBottom: '8px'}}>
                                    The Merchant Center UI for developer registration is missing for many accounts. You can register directly from here instead — paste the merchant ID and click Register. Requires that your connected Google account has Admin role on that merchant.
                                </Typography>

                                <Stack direction="row" gap="8px" sx={{alignItems: 'center', flexWrap: 'wrap'}}>
                                    <TextField
                                        size="small"
                                        placeholder="Merchant ID (e.g. 5655842983)"
                                        value={registerMerchantId}
                                        onChange={(event) => setRegisterMerchantId(event.target.value)}
                                        disabled={registerState.status === 'loading'}
                                        InputProps={compactInputProps}
                                        sx={{flex: 1, minWidth: 200}}
                                    />
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleRegisterDeveloper}
                                        disabled={registerState.status === 'loading' || !registerMerchantId.trim()}
                                        sx={{fontSize: 12}}>
                                        {registerState.status === 'loading' ? 'Registering…' : 'Register'}
                                    </Button>
                                </Stack>

                                {registerState.status === 'success' && (
                                    <Typography sx={{fontSize: 11.5, color: C.teal, marginTop: '8px', lineHeight: 1.5}}>
                                        ✓ {registerState.message}
                                    </Typography>
                                )}
                                {registerState.status === 'error' && (
                                    <Typography sx={{fontSize: 11.5, color: C.coral, marginTop: '8px', lineHeight: 1.5}}>
                                        {registerState.message}
                                    </Typography>
                                )}
                            </Alert>
                        )}

                        <Box>
                            <Typography sx={sectionTitleStyle}>Account</Typography>
                            <Stack gap="10px" sx={{marginTop: '8px'}}>
                                <Box>
                                    <Typography sx={labelStyle}>Merchant ID</Typography>
                                    {renderMerchantField()}
                                    {renderMerchantHelper()}
                                </Box>
                                <Box>
                                    <Typography sx={labelStyle}>Supplemental data sources</Typography>
                                    {renderDetectedSources()}
                                </Box>
                                <Box>
                                    <Typography sx={labelStyle}>Offer ID template</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={draft.offerIdTemplate}
                                        onChange={handleField('offerIdTemplate')}
                                        InputProps={compactInputProps}
                                    />
                                    <Typography sx={{fontSize: 11, color: C.textFaint, marginTop: '4px'}}>
                                        Variables: <code>{'{COUNTRY}'}</code>, <code>{'{PRODUCT_ID}'}</code>, <code>{'{VARIANT_ID}'}</code>.
                                        <code>{'{COUNTRY}'}</code> is used to parse the feed label from each offer ID at push time.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {saveError && <Alert severity="error" sx={{fontSize: 12.5}}>{saveError}</Alert>}
                        {saveOk && !dirty && (
                            <Alert severity="success" sx={{fontSize: 12.5}}>Settings saved.</Alert>
                        )}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{padding: '12px 20px', background: C.surface}}>
                <Button onClick={onClose} color="inherit">Close</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || !dirty || loading}>
                    {saving ? 'Saving…' : 'Save settings'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
